import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tokenStore } from '@/lib/token-store';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { readStash } from '@/lib/impersonation-stash';
import { jwtClaims } from '@/lib/jwt';

/** Minutes left at or under which the banner turns red. */
const URGENT_MINUTES = 5;

/** An impersonation lasts 30 minutes. */
const SESSION_MINUTES = 30;

/**
 * Whole minutes left, rounded down so a 30 minute session never reads "31 min left" (the end time arrives a moment after the start).
 * Any time left shows as at least 1: 0 means over, and the banner stops the session at 0.
 */
function minutesLeft(ms: number): number {
  // Capped at the session length: an end time a little ahead of 30 minutes (latency, clock skew) must never read more.
  return ms <= 0 ? 0 : Math.min(SESSION_MINUTES, Math.max(1, Math.floor(ms / 60_000)));
}

/** Epoch ms at which the impersonation ends: the end time stored when it started (the server's clock, from `expires_at`), else the token's `exp`. */
function endsAt(): number | null {
  const stored = readStash()?.expiresAt;
  if (typeof stored === 'number') return stored;
  const exp = jwtClaims(tokenStore.getAccess())?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

/**
 * Sticks to the top of every page while a platform admin is impersonating: whose account, by whom, minutes left, and Stop. The token
 * lasts 30 minutes (the server refuses it after that); the banner stops the impersonation itself when the time is up.
 * It is shown inside the customer app, where the platform namespace may not be loaded yet, so it never suspends and every string has an
 * English default.
 */
export function ImpersonationBanner() {
  const { t, i18n } = useTranslation('platform', { useSuspense: false });
  const { impersonation, user, stopImpersonation } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [stopping, setStopping] = useState(false);
  const [failed, setFailed] = useState(false);
  const autoStopped = useRef(false);
  const bar = useRef<HTMLDivElement>(null);

  // Fixed chrome (the sidebar) starts below the banner: publish its height as --impersonation-banner-h.
  useLayoutEffect(() => {
    const el = bar.current;
    if (!impersonation || !el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--impersonation-banner-h', `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--impersonation-banner-h');
    };
  }, [impersonation]);
  useEffect(() => {
    if (impersonation && !i18n.hasLoadedNamespace('platform')) void i18n.loadNamespaces('platform');
  }, [impersonation, i18n]);
  useEffect(() => {
    if (!impersonation) return;
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [impersonation]);

  const end = impersonation ? endsAt() : null;
  // The cookie lapses at about the token's end, so a missing token while impersonating means the session is over.
  const tokenGone = impersonation !== null && !tokenStore.getAccess();
  const minutes = tokenGone ? 0 : end === null ? null : minutesLeft(end - now);

  // Wake up exactly at the end instead of waiting for the next tick.
  useEffect(() => {
    if (!impersonation || end === null) return;
    const wait = end - Date.now();
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setNow(Date.now()), wait + 50);
    return () => window.clearTimeout(timer);
  }, [impersonation, end]);

  const stop = async () => {
    setStopping(true);
    setFailed(false);
    try {
      await stopImpersonation();
    } catch {
      setFailed(true);
    } finally {
      setStopping(false);
    }
  };

  useEffect(() => {
    if (!impersonation) {
      autoStopped.current = false;
      return;
    }
    if (minutes === 0 && !autoStopped.current) {
      autoStopped.current = true;
      void stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonation, minutes]);

  if (!impersonation) return null;
  const urgent = minutes !== null && minutes <= URGENT_MINUTES;
  const email = user?.email ?? '';
  return (
    <div
      ref={bar}
      role="status"
      data-urgent={urgent ? 'true' : undefined}
      className={`sticky top-0 inset-x-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-1.5 text-xs font-medium shadow ${
        urgent ? 'bg-red-600 text-white' : 'bg-amber-500 text-black'
      }`}
    >
      <span className="min-w-0 text-center">
        {t('banner.text', {
          account: (readStash()?.accountName ?? email),
          email,
          admin: impersonation.impersonatorEmail,
          defaultValue: 'Viewing {{account}} as {{email}}, by {{admin}}. Every change is recorded.',
        })}
      </span>
      {minutes !== null && (
        <span className="shrink-0 font-semibold tabular-nums">
          {minutes === 0
            ? t('banner.expired', { defaultValue: 'Session ended' })
            : t('banner.endsIn', { minutes, defaultValue: '{{minutes}} min left' })}
        </span>
      )}
      {failed && (
        <span role="alert" className="shrink-0 font-semibold underline">
          {t('banner.stopFailed', { defaultValue: 'Could not stop impersonating. Try again.' })}
        </span>
      )}
      <button
        type="button"
        onClick={stop}
        disabled={stopping}
        className="shrink-0 rounded bg-black/80 px-2.5 py-0.5 font-semibold text-white hover:bg-black disabled:opacity-60"
      >
        {stopping ? t('banner.stopping', { defaultValue: 'Stopping…' }) : t('banner.stop', { defaultValue: 'Stop impersonating' })}
      </button>
    </div>
  );
}
